import { CheckCircle, Award, Heart, Shield, Scale } from 'lucide-react';
import { aboutContent } from '../mock';

export const About = () => {
  const valueIcons = {
    Expertise: Award,
    Compassion: Heart,
    Integrity: Shield,
    Results: CheckCircle
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {aboutContent.title}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Dedicated to helping families achieve their immigration goals with expertise and compassion
            </p>
          </div>
        </div>
      </section>

      {/* Main About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <div className="relative">
              <img
                src={aboutContent.image}
                alt="Gabriela Mendoza Thibeau"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-gold-400 to-gold-600 text-navy-900 p-8 rounded-xl shadow-2xl">
                <p className="text-5xl font-bold">25+</p>
                <p className="text-sm font-semibold mt-1">Years of Dedication</p>
              </div>
            </div>

            <div>
              <h2 className="text-4xl font-bold text-navy-900 mb-6">
                Committed to Your Success
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {aboutContent.mainText}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                {aboutContent.mission}
              </p>
            </div>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {aboutContent.stats.map((stat, index) => (
              <div
                key={index}
                className="text-center p-6 bg-gradient-to-br from-navy-50 to-gold-50 rounded-2xl hover:shadow-lg transition-all duration-300"
              >
                <p className="text-4xl md:text-5xl font-bold text-navy-900 mb-2">
                  {stat.number}
                </p>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {aboutContent.values.map((value, index) => {
              const IconComponent = valueIcons[value.title];
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-gold-400 to-gold-600 rounded-xl mb-6 shadow-lg">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-16 bg-gradient-to-br from-navy-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-5xl mx-auto">
            {/* Bar Admissions */}
            <div className="text-center mb-12">
              <h3 className="text-2xl font-bold text-navy-900 mb-8">
                Bar Admissions & Credentials
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-gold-200">
                  <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-navy-700 to-navy-900 rounded-full flex items-center justify-center">
                    <Scale className="w-10 h-10 text-gold-400" />
                  </div>
                  <h4 className="text-lg font-bold text-navy-900 mb-2">State of Ohio</h4>
                  <p className="text-sm text-gray-600">Licensed Attorney</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-gold-200">
                  <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-navy-700 to-navy-900 rounded-full flex items-center justify-center">
                    <Scale className="w-10 h-10 text-gold-400" />
                  </div>
                  <h4 className="text-lg font-bold text-navy-900 mb-2">Commonwealth of Massachusetts</h4>
                  <p className="text-sm text-gray-600">Licensed Attorney</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-gold-200">
                  <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-navy-700 to-navy-900 rounded-full flex items-center justify-center">
                    <Scale className="w-10 h-10 text-gold-400" />
                  </div>
                  <h4 className="text-lg font-bold text-navy-900 mb-2">U.S. Supreme Court</h4>
                  <p className="text-sm text-gray-600">Admitted to Practice</p>
                </div>
              </div>
            </div>

            {/* Professional Memberships */}
            <div className="text-center mt-16">
              <h3 className="text-2xl font-bold text-navy-900 mb-8">
                Professional Memberships
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-navy-200">
                  <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full flex items-center justify-center">
                    <Award className="w-10 h-10 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-navy-900 mb-2">Butler County Bar Association</h4>
                  <p className="text-sm text-gray-600">Member</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-navy-200">
                  <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full flex items-center justify-center">
                    <Award className="w-10 h-10 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-navy-900 mb-2">Cincinnati Bar Association</h4>
                  <p className="text-sm text-gray-600">Member</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-navy-200">
                  <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full flex items-center justify-center">
                    <Award className="w-10 h-10 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-navy-900 mb-2">American Immigration Lawyers Association</h4>
                  <p className="text-sm text-gray-600">Member (AILA)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Background Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-navy-900 mb-8 text-center">
              Experience That Makes a Difference
            </h2>
            
            <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
              <p>
                Gabriela Mendoza Thibeau has dedicated her career to helping individuals and families navigate the complex world of U.S. immigration law. With over 25 years of experience, she has successfully represented clients in a wide range of immigration matters.
              </p>
              
              <p>
                As a bilingual attorney fluent in English and Spanish, Gabriela understands the unique challenges faced by immigrant communities. She takes pride in providing personalized legal services that address each client's specific needs and circumstances.
              </p>
              
              <p>
                Her approach combines deep legal knowledge with genuine compassion. She believes that behind every immigration case is a person with hopes, dreams, and the courage to build a better future. This philosophy drives her commitment to fighting for her clients' rights and pursuing the best possible outcomes.
              </p>

              <div className="bg-gradient-to-br from-navy-50 to-gold-50 p-8 rounded-2xl mt-8 border-l-4 border-gold-500">
                <p className="text-navy-900 font-medium italic">
                  "Immigration law is not just my profession—it's my passion. Every successful case represents a family reunited, a dream realized, and a future secured. That's what motivates me every single day."
                </p>
                <p className="text-navy-700 font-semibold mt-4">
                  — Gabriela Mendoza Thibeau
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Reviews Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-navy-900 mb-6">
              Verified Client Reviews
            </h2>
            <p className="text-lg text-gray-600 mb-12">
              Don't just take our word for it. Read what our clients have to say about their experience working with our firm.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Google Reviews Card */}
              <a
                href="https://g.page/r/CYt8TYXxDplgEAE/review"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border-2 border-gray-200 hover:border-gold-400 shadow-md hover:shadow-xl transition-all duration-300 group"
              >
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                    </svg>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">Google Reviews</h3>
                <div className="flex justify-center space-x-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-gold-600 font-semibold group-hover:text-gold-700">
                  View Google Reviews →
                </p>
              </a>

              {/* Facebook Reviews Card */}
              <a
                href="https://www.facebook.com/GMThibeau/reviews/?id=100064695380009&sk=reviews"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border-2 border-gray-200 hover:border-gold-400 shadow-md hover:shadow-xl transition-all duration-300 group"
              >
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">Facebook Reviews</h3>
                <div className="flex justify-center space-x-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-gold-600 font-semibold group-hover:text-gold-700">
                  View Facebook Reviews →
                </p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Let's Work Together
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Experience the difference that dedicated, compassionate legal representation can make in your immigration journey.
            </p>
            <a
              href="/contact"
              className="inline-block bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-navy-900 px-10 py-4 text-lg font-bold rounded-xl shadow-2xl hover:shadow-gold-500/50 transition-all duration-300 hover:scale-105"
            >
              Schedule Your Consultation
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
